import React, { useState, useMemo } from 'react';
import { Search, AlertCircle, Calendar, Trash2, Edit } from 'lucide-react';
import { DataTable, Column } from '../common/DataTable';
import { useSalesHistory, useDeleteSale } from '../../hooks/useSales';
import { Sale, SaleFormData } from '../../types/Sales';
import { formatDateTime } from '../../utils/formatters';
import { SalesModal } from '../sales/SalesModal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { useProducts } from '../../hooks/useProducts';
import { getErrorMessage } from '../../utils/errorMessages';


export const SalesHistoryTab: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [barcode, setBarcode] = useState('');
  const [searchParams, setSearchParams] = useState<{
    startDate: string;
    endDate: string;
    barcode?: string;
  }>({
    startDate: '',
    endDate: '',
  });
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [localSales, setLocalSales] = useState<Sale[]>([]);

  const { data: history = [], isLoading, isError } = useSalesHistory(searchParams);
  const { data: productsPage } = useProducts({ page: 0, size: 1000 });
  const deleteSale = useDeleteSale();
  
  const products = useMemo(() => productsPage?.content || [], [productsPage?.content]);

  const salesList = useMemo(() => {
    const historySales: Sale[] = history.map((item) => {
      const product = products.find(p => p.name === item.productName);
      return {
        id: item.id,
        productId: product?.id || 0,
        productName: item.productName,
        productBrand: item.productBrand,
        quantitySold: item.quantitySold,
        totalEarning: Number(item.totalEarning) || 0,
        saleDate: item.saleDate,
      };
    });
    
    const historyIds = new Set(historySales.map(s => s.id));
    const localOnly = localSales.filter(s => !historyIds.has(s.id));
    return [...historySales, ...localOnly];
  }, [history, products, localSales]);

  const handleSearch = () => {
    if (!startDate || !endDate) {
      return; // Backend'de startDate ve endDate required
    }
    setSearchParams({
      startDate,
      endDate,
      barcode: barcode || undefined,
    });
    setSelectedSale(null); // Reset selection on new search
  };

  const handleRowClick = (sale: Sale) => {
    // Satış geçmişi satırına tıklandığında satışı iptal etme diyaloğunu aç
    setSelectedSale(sale);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (sale: Sale) => {
    setSelectedSale(sale);
    setModalOpen(true);
  };

  const handleDeleteClick = () => {
    if (selectedSale) {
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedSale) {
      try {
        await deleteSale.mutateAsync(selectedSale.id);
        // Also remove from local sales
        setLocalSales(prev => prev.filter(s => s.id !== selectedSale.id));
        setToast({
          open: true,
          message: 'Satış başarıyla iptal edildi ve ürün stoğu geri yüklendi.',
          severity: 'success',
        });
        setDeleteDialogOpen(false);
        setSelectedSale(null);
      } catch (error: any) {
        setToast({ 
          open: true, 
          message: getErrorMessage(error, 'Satış silinirken hata oluştu'), 
          severity: 'error' 
        });
      }
    }
  };

  const handleSave = (data: SaleFormData) => {
    if (selectedSale) {
      // Frontend-only: Update local sales
      const updatedSale: Sale = {
        ...selectedSale,
        ...data,
        productName: products.find(p => p.id === data.productId)?.name || selectedSale.productName,
        productBrand: products.find(p => p.id === data.productId)?.brand || selectedSale.productBrand,
      };
      setLocalSales(prev => {
        const filtered = prev.filter(s => s.id !== selectedSale.id);
        return [...filtered, updatedSale];
      });
      setToast({ open: true, message: 'Satış başarıyla güncellendi', severity: 'success' });
    }
    setModalOpen(false);
    setSelectedSale(null);
  };

  const columns: Column<Sale>[] = [
    { id: 'productName', label: 'Ürün Adı', minWidth: 150 },
    { id: 'productBrand', label: 'Marka', minWidth: 100 },
    {
      id: 'saleDate',
      label: 'Satış Tarihi',
      minWidth: 150,
      format: (value) => value ? formatDateTime(value as string) : '-',
    },
    { id: 'quantitySold', label: 'Miktar', minWidth: 80, align: 'right' },
    {
      id: 'totalEarning',
      label: 'Toplam Tutar',
      minWidth: 120,
      align: 'right',
      format: (value) => `${(value as number).toFixed(2)} ₺`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {isError && (
        <div className="card p-4 bg-warning/10 border-warning/20 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
          <p className="text-sm text-foreground-secondary">
            Backend bağlantısı kurulamadı. Veriler yüklenemiyor.
          </p>
        </div>
      )}

      {/* Header with Action Buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Satış Geçmişi</h3>
        <div className="flex items-center gap-3">
          {selectedSale && (
            <>
              <button
                onClick={() => handleEdit(selectedSale)}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Düzenle
              </button>
              <button
                onClick={handleDeleteClick}
                className="btn-danger flex items-center gap-2"
                disabled={!selectedSale}
              >
                <Trash2 className="w-4 h-4" />
                Sil
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 flex items-center gap-2 h-6">
              <Calendar className="w-4 h-4 text-foreground-tertiary" />
              Başlangıç Tarihi
            </label>
            <div className="relative flex-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input pr-10 h-9 md:h-10 w-full"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 flex items-center gap-2 h-6">
              <Calendar className="w-4 h-4 text-foreground-tertiary" />
              Bitiş Tarihi
            </label>
            <div className="relative flex-1">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input pr-10 h-9 md:h-10 w-full"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 h-6">
              Barkod
            </label>
            <input
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              className="input h-10 w-full"
            />
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 h-6 invisible">
              Ara
            </label>
            <button
              onClick={handleSearch}
              className="btn-primary w-full flex items-center justify-center gap-2 h-10"
            >
              <Search className="w-4 h-4" />
              Ara
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={salesList}
        loading={isLoading}
        onRowClick={handleRowClick}
        onRowDoubleClick={handleEdit}
        getRowId={(row: Sale) => row.id}
        selectedRowId={selectedSale?.id}
      />

      {/* Modals */}
      {selectedSale && (
        <SalesModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedSale(null);
          }}
          onSave={handleSave}
          sale={selectedSale}
          products={products}
          loading={false}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedSale(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Satışı İptal Et"
        message={`Bu satışı iptal etmek istediğinize emin misiniz? Satılan ürünlerin stoğu geri eklenecektir.`}
        loading={deleteSale.isPending}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />
    </div>
  );
};
